import { z } from 'zod'

import { ApiClientError } from '@ems/api-client/client'
import { toInstant, toLocalInput } from '@ems/contracts/timezones'

import { api } from '#admin/lib/api-client.ts'

/**
 * The ticket-type catalog: what an edition sells, and the one fetcher every picker reads.
 *
 * The walk-in picker and the registration form read this same list, under the same query key, so
 * there is one schema for it. Two fetchers under one key would let whichever ran last decide what
 * shape the cache held.
 */

// ponytail: mirrors `TICKET_CATEGORIES` in @ems/db, which admin does not depend on.
export const TICKET_CATEGORIES = [
  'delegate',
  'exhibitor_pass',
  'speaker',
  'media',
  'visitor',
  'staff',
] as const

/** "exhibitor_pass" → "Exhibitor pass". */
export function categoryLabel(category: string): string {
  const spaced = category.replaceAll('_', ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * One picker option per ticket type. A name is not unique — the v1 import keeps one type per v1
 * activity, so an edition can sell two "Media" passes — and a shared name carries its code, which
 * is unique. A name nobody else has stays bare.
 */
export function ticketTypeOptions(
  types: readonly { id: string; name: string; code: string }[],
): { value: string; label: string }[] {
  const uses = new Map<string, number>()
  for (const type of types) uses.set(type.name, (uses.get(type.name) ?? 0) + 1)
  return types.map((type) => ({
    value: type.id,
    label: (uses.get(type.name) ?? 0) > 1 ? `${type.name} (${type.code})` : type.name,
  }))
}

const ticketTypeSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: z.enum(TICKET_CATEGORIES),
  price: z.number(),
  currency: z.string(),
  taxRate: z.string(),
  requiresVerification: z.boolean(),
  // `.default(null)`: the console is deployed ahead of the API, whose answer may not carry it yet.
  verificationInstructions: z.string().nullable().default(null),
  isMemberPackage: z.boolean(),
  requiredAssociationName: z.string().nullable(),
  grantsSpeakerAccess: z.boolean(),
  grantsBusinessMatching: z.boolean(),
  /** A stand can issue it as a convention pass. `.default`: the console can be deployed first. */
  forExhibitors: z.boolean().default(false),
  capacity: z.number().nullable(),
  /** Tickets issued. */
  soldCount: z.number(),
  /** Seats taken by submitted registrations, paid or not. Capacity is checked against this. */
  held: z.number(),
  remaining: z.number().nullable(),
  salesStartAt: z.string().nullable(),
  salesEndAt: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number(),
})

export type TicketType = z.infer<typeof ticketTypeSchema>

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {})
const base = '/v1/ticket/admin/ticket-types'

/** Every type, inactive ones included, in the server's order: `sortOrder`, then name. */
export function fetchTicketTypes(signal?: AbortSignal) {
  return api.get(base, {
    schema: z.object({ items: z.array(ticketTypeSchema) }),
    ...withSignal(signal),
  })
}

/** What the dialog edits: every input as its own string, as the controls hold it. */
export type TicketTypeDraft = {
  code: string
  name: string
  description: string
  category: TicketType['category']
  /** A decimal string. Money goes over the wire as one, never as a float. */
  price: string
  currency: string
  taxRate: string
  isMemberPackage: boolean
  requiredAssociationName: string
  requiresVerification: boolean
  /** What a participant must give for the verification. Shown to them in the portal. */
  verificationInstructions: string
  /** Empty means unlimited. */
  capacity: string
  /** `datetime-local` values, in the edition's zone. Empty means no limit. */
  salesStartAt: string
  salesEndAt: string
  isActive: boolean
  grantsSpeakerAccess: boolean
  grantsBusinessMatching: boolean
  forExhibitors: boolean
}

export function draftOf(type: TicketType | null, currency: string, zone: string): TicketTypeDraft {
  if (type === null) {
    return {
      code: '',
      name: '',
      description: '',
      category: 'delegate',
      price: '0',
      currency,
      taxRate: '0',
      isMemberPackage: false,
      requiredAssociationName: '',
      requiresVerification: false,
      verificationInstructions: '',
      capacity: '',
      salesStartAt: '',
      salesEndAt: '',
      isActive: true,
      grantsSpeakerAccess: false,
      grantsBusinessMatching: false,
      forExhibitors: false,
    }
  }
  return {
    code: type.code,
    name: type.name,
    description: type.description ?? '',
    category: type.category,
    price: String(type.price),
    currency: type.currency,
    taxRate: type.taxRate,
    isMemberPackage: type.isMemberPackage,
    requiredAssociationName: type.requiredAssociationName ?? '',
    requiresVerification: type.requiresVerification,
    verificationInstructions: type.verificationInstructions ?? '',
    capacity: type.capacity === null ? '' : String(type.capacity),
    salesStartAt: type.salesStartAt === null ? '' : toLocalInput(type.salesStartAt, zone),
    salesEndAt: type.salesEndAt === null ? '' : toLocalInput(type.salesEndAt, zone),
    isActive: type.isActive,
    grantsSpeakerAccess: type.grantsSpeakerAccess,
    grantsBusinessMatching: type.grantsBusinessMatching,
    forExhibitors: type.forExhibitors,
  }
}

// The columns' own limits: price is `numeric(18,4)`, the tax rate a percentage in `numeric(9,4)`.
const PRICE = /^\d{1,14}(?:\.\d{1,4})?$/
const RATE = /^\d{1,3}(?:\.\d{1,4})?$/

/** What is wrong with a draft, by field. Empty when it can be sent. */
export function draftIssues(draft: TicketTypeDraft): Record<string, string> {
  const issues: Record<string, string> = {}
  if (draft.code.trim() === '') issues['code'] = 'Give the type a code.'
  if (draft.name.trim() === '') issues['name'] = 'Give the type a name.'
  if (!PRICE.test(draft.price.trim())) issues['price'] = 'Enter a price such as 1500000 or 0.'
  if (!RATE.test(draft.taxRate.trim()) || Number(draft.taxRate) > 100) {
    issues['taxRate'] = 'Enter a percentage from 0 to 100, such as 11.'
  }
  if (draft.capacity.trim() !== '' && !/^[1-9]\d*$/.test(draft.capacity.trim())) {
    issues['capacity'] = 'Enter a whole number of seats, or leave it empty for unlimited.'
  }
  // `datetime-local` values sort as strings, and both are in the same zone.
  if (
    draft.salesStartAt !== '' &&
    draft.salesEndAt !== '' &&
    draft.salesEndAt <= draft.salesStartAt
  ) {
    issues['salesEndAt'] = 'Sales must end after they start.'
  }
  return issues
}

/** Whether the type can be marked for exhibitors: a free type that is not an exhibitor pass. */
export function offersToExhibitors(draft: Pick<TicketTypeDraft, 'category' | 'price'>): boolean {
  return (
    draft.category !== 'exhibitor_pass' &&
    PRICE.test(draft.price.trim()) &&
    Number(draft.price) === 0
  )
}

const textOrNull = (value: string) => (value.trim() === '' ? null : value.trim())

/**
 * The wire body. Every field is sent, so an emptied input clears its column (`null`) rather than
 * being left out and kept — the update treats absent and null differently on purpose.
 */
export function bodyOf(draft: TicketTypeDraft, zone: string) {
  const instant = (value: string) => (value === '' ? null : toInstant(value, zone))
  return {
    code: draft.code.trim(),
    name: draft.name.trim(),
    description: textOrNull(draft.description),
    category: draft.category,
    price: draft.price.trim(),
    currency: draft.currency,
    taxRate: draft.taxRate.trim(),
    isMemberPackage: draft.isMemberPackage,
    requiredAssociationName: textOrNull(draft.requiredAssociationName),
    requiresVerification: draft.requiresVerification,
    // Kept with the switch off, so switching it back on does not lose the text.
    verificationInstructions: textOrNull(draft.verificationInstructions),
    capacity: draft.capacity.trim() === '' ? null : Number(draft.capacity),
    salesStartAt: instant(draft.salesStartAt),
    salesEndAt: instant(draft.salesEndAt),
    isActive: draft.isActive,
    // The API refuses the grant on any other category. The form hides it there, so moving the
    // category away must not send its stale switch.
    grantsSpeakerAccess: draft.category === 'speaker' && draft.grantsSpeakerAccess,
    grantsBusinessMatching: draft.grantsBusinessMatching,
    // The API refuses the flag on a priced type, and an exhibitor pass is offered to stands
    // already. The form hides the switch for both, so its stale value is not sent.
    forExhibitors: offersToExhibitors(draft) && draft.forExhibitors,
  }
}

export type TicketTypeBody = ReturnType<typeof bodyOf>

/**
 * An edit's PATCH: only the fields the operator changed. Sending the whole body would write back
 * the copy taken when the dialog opened, so a type somebody deactivated meanwhile would be
 * reactivated by a typo fix.
 */
export function changesOf(
  type: TicketType,
  draft: TicketTypeDraft,
  zone: string,
): Partial<TicketTypeBody> {
  const before: Record<string, unknown> = bodyOf(draftOf(type, type.currency, zone), zone)
  return Object.fromEntries(
    Object.entries(bodyOf(draft, zone)).filter(([key, value]) => before[key] !== value),
  )
}

/** A new type goes to the end of the list, after the ones the operator has already ordered. */
export function createTicketType(body: TicketTypeBody & { sortOrder: number }) {
  return api.post(base, { body, schema: ticketTypeSchema })
}

export function updateTicketType(id: string, body: Partial<TicketTypeBody>) {
  return api.patch(`${base}/${id}`, { body, schema: ticketTypeSchema })
}

/** Every type's id, in the new order. The server refuses a list that is not exactly the types. */
export function reorderTicketTypes(ids: readonly string[]) {
  return api.put(`${base}/order`, { body: { ids }, schema: z.undefined() })
}

export function deleteTicketType(id: string) {
  return api.delete(`${base}/${id}`, { schema: z.undefined() })
}

/**
 * Which dialog field a refusal belongs beside, read from the details the API names. `undefined`
 * sends it to a toast instead.
 */
export function refusalField(error: unknown): keyof TicketTypeDraft | undefined {
  if (!(error instanceof ApiClientError)) return undefined
  const detail = error.detail ?? {}
  if (error.code === 'conflict') {
    if ('requiresModule' in detail) return 'price'
    if ('held' in detail) return 'capacity'
    if ('currency' in detail) return 'currency'
    if ('code' in detail) return 'code'
  }
  if (error.code === 'validation_failed' && 'salesEndAt' in detail) return 'salesEndAt'
  return undefined
}

/** A delete refused because the type has registrations — the operator should deactivate it. */
export function isInUseRefusal(error: unknown): boolean {
  return error instanceof ApiClientError && error.code === 'conflict'
}

/**
 * One edge of a sales or validity window, in the edition's zone — the zone the dialog edits it in
 * (decision 4), so the list and the form agree on a laptop set to another zone — and with the year
 * when it is not this one, so a v1 voucher that expired in 2020 does not read as this January.
 */
export function windowDate(instant: string, zone: string, now: Date): string {
  const at = new Date(instant)
  if (Number.isNaN(at.getTime())) return instant
  const year = (d: Date) =>
    new Intl.DateTimeFormat('en', { timeZone: zone, year: 'numeric' }).format(d)
  return at.toLocaleString(undefined, {
    timeZone: zone,
    day: '2-digit',
    month: 'short',
    ...(year(at) === year(now) ? {} : { year: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** "on sale", "starts 01 Mar, 09:00", "ended 01 Apr 2025, 09:00", or "always". */
export function salesWindowLabel(
  type: Pick<TicketType, 'salesStartAt' | 'salesEndAt'>,
  now: Date,
  zone: string,
): string {
  if (type.salesStartAt === null && type.salesEndAt === null) return 'always'
  if (type.salesStartAt !== null && Date.parse(type.salesStartAt) > now.getTime()) {
    return `starts ${windowDate(type.salesStartAt, zone, now)}`
  }
  if (type.salesEndAt !== null && Date.parse(type.salesEndAt) <= now.getTime()) {
    return `ended ${windowDate(type.salesEndAt, zone, now)}`
  }
  return 'on sale'
}

/**
 * "37 of 50 held · 30 issued". Both numbers, because they answer different questions: held is
 * what capacity is checked against, so the capacity sits beside it; issued is what has been paid
 * for and ticketed.
 */
export function capacityLabel(type: Pick<TicketType, 'held' | 'soldCount' | 'capacity'>): string {
  const issued = `${type.soldCount} issued`
  return type.capacity === null
    ? `${type.held} held · ${issued} · No limit`
    : `${type.held} of ${type.capacity} held · ${issued}`
}
